// YDocSyncProvider — binds a Y.Doc to a pluggable transport.
//
// This is the heart of Option B and is deliberately transport-agnostic: the SAME
// provider drives the spike (LoopbackTransport, in-memory) and production
// (CentrifugoTransport → your Python /docs API + Centrifugo `doc:{id}` channel).
// Nothing here depends on BlockSuite — it operates purely on the underlying Y.Doc,
// so it transfers across BlockSuite versions (0.19 turnkey or 0.22 modular).
import * as Y from 'yjs';

/** Pluggable persistence + fan-out for a single collaborative document. */
export interface DocSyncTransport {
  /** Stable id for THIS client; stamped on sends so we can skip our own echoes. */
  readonly clientId: string;
  /** Persisted state as an ordered list of yjs update blobs. */
  load(docId: string): Promise<Uint8Array[]>;
  /** Persist + fan out a local update (backend appends and publishes to subscribers). */
  send(docId: string, update: Uint8Array): void | Promise<void>;
  /** Receive remote updates. `origin` is the sender's clientId, for echo-skipping.
   *  Returns an unsubscribe function. */
  subscribe(docId: string, onRemote: (update: Uint8Array, origin?: string) => void): () => void;
}

const REMOTE = 'remote'; // yjs transaction origin marking updates we received (not to be re-sent)

export class YDocSyncProvider {
  readonly doc: Y.Doc;
  private readonly docId: string;
  private readonly transport: DocSyncTransport;
  private unsub: (() => void) | null = null;
  private destroyed = false;
  private _synced = false;
  private readonly syncHandlers = new Set<(synced: boolean) => void>();

  constructor(doc: Y.Doc, docId: string, transport: DocSyncTransport) {
    this.doc = doc;
    this.docId = docId;
    this.transport = transport;
    this.onLocalUpdate = this.onLocalUpdate.bind(this);
  }

  /** Wire up the doc: subscribe → load history → start streaming local edits. */
  async connect(): Promise<void> {
    // 1) Subscribe FIRST, so updates arriving mid-load aren't lost.
    this.unsub = this.transport.subscribe(this.docId, (update, origin) => {
      if (this.destroyed) return;
      if (origin && origin === this.transport.clientId) return; // our own echo — ignore
      Y.applyUpdate(this.doc, update, REMOTE);
    });

    // 2) Load persisted history and apply it (origin=REMOTE ⇒ not re-sent in step 3).
    const history = await this.transport.load(this.docId);
    if (this.destroyed) return;
    for (const u of history) Y.applyUpdate(this.doc, u, REMOTE);

    // 3) Stream local edits to the backend. Remote-origin updates are filtered out.
    this.doc.on('update', this.onLocalUpdate);
    this.setSynced(true);
  }

  private onLocalUpdate(update: Uint8Array, origin: unknown): void {
    if (origin === REMOTE) return;          // never echo a remote update back
    void this.transport.send(this.docId, update);
  }

  /** Notify on initial-sync completion (for a "loading…" state on the editor). */
  onSync(cb: (synced: boolean) => void): () => void {
    this.syncHandlers.add(cb);
    cb(this._synced);
    return () => this.syncHandlers.delete(cb);
  }
  private setSynced(v: boolean): void {
    this._synced = v;
    this.syncHandlers.forEach((h) => h(v));
  }

  get synced(): boolean {
    return this._synced;
  }

  destroy(): void {
    this.destroyed = true;
    this.doc.off('update', this.onLocalUpdate);
    this.unsub?.();
    this.unsub = null;
    this.syncHandlers.clear();
  }
}
